/**
 * Certificación DGII — Fase 2 (Set de Pruebas)
 *
 * Permite:
 *   - Subir el .xlsx del Set de Pruebas descargado del portal DGII.
 *   - Ver los 25 e-CF + 4 RFCE parseados.
 *   - Autenticarse con DGII (obtener JWT).
 *   - Previsualizar el XML generado por caso.
 *   - Enviar casos uno a uno o en lote.
 *   - Consultar el estado del comprobante (TrackId) con la DGII.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Upload, PlayCircle, Rocket, RefreshCw, Eye, Loader2, CheckCircle2,
  XCircle, AlertCircle, FileText, Send, Trash2, Download, FileSearch,
  Copy, FileArchive, LifeBuoy, RotateCcw, AlertTriangle,
} from "lucide-react";

const STATUS_META = {
  pending:       { color: "bg-muted text-muted-foreground",   Icon: AlertCircle,  label: "Pendiente" },
  sent:          { color: "bg-blue-500/15 text-blue-700",      Icon: Send,         label: "Enviado"   },
  approved:      { color: "bg-success/15 text-success",        Icon: CheckCircle2, label: "Aprobado"  },
  rejected:      { color: "bg-destructive/15 text-destructive",Icon: XCircle,      label: "Rechazado" },
  error:         { color: "bg-amber-500/15 text-amber-700",    Icon: AlertCircle,  label: "Error"     },
  manual_upload: { color: "bg-amber-500/15 text-amber-700",    Icon: Upload,       label: "Upload manual" },
  already_used:  { color: "bg-amber-500/15 text-amber-700",    Icon: AlertTriangle, label: "Ya registrado en DGII" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold ${m.color}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function DGIICertificationTab() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [session, setSession] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const refreshAllStatus = async () => {
    setRefreshingAll(true);
    try {
      const { data } = await api.post("/dgii/certification/refresh-all-status");
      const rfce = data.reconciled_rfce || 0;
      let msg = `${data.updated}/${data.total} casos actualizados`;
      if (rfce > 0) msg += ` · ${rfce} RFCE conciliados`;
      toast.success(msg);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRefreshingAll(false); }
  };
  const [sendingAll, setSendingAll] = useState(false);
  const [previewXml, setPreviewXml] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  // Soporte DGII
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportData, setSupportData] = useState(null);  // { generated_at, total, totals, cases }
  const [supportFilter, setSupportFilter] = useState("rejected"); // 'all' | 'rejected' | 'approved'
  const [supportExpanded, setSupportExpanded] = useState({}); // { [case_id]: true }

  const loadCases = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dgii/certification/cases");
      setCases(data.cases || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadSession = async () => {
    try {
      const { data } = await api.get("/dgii/certification/session");
      setSession(data);
    } catch { setSession(null); }
  };

  useEffect(() => { loadCases(); loadSession(); }, []);

  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const fresh = window.confirm(
      "¿Reiniciaste el proceso en el portal DGII (contadores en 0)?\n\n" +
      "Aceptar = SÍ: se empieza de cero; los resúmenes (RFCE) se enviarán con un código de seguridad NUEVO.\n" +
      "Cancelar = NO: se conservan los resúmenes ya aceptados y sus firmas."
    );
    fd.append("fresh_start", fresh ? "true" : "false");
    setUploading(true);
    try {
      const { data } = await api.post("/dgii/certification/upload-set", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Cargados ${data.count} casos (${data.summary?.ECF?.total || 0} e-CF + ${data.summary?.RFCE?.total || 0} RFCE)`);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); ev.target.value = ""; }
  };

  const authenticate = async () => {
    try {
      const { data } = await api.post("/dgii/certification/authenticate");
      toast.success(`JWT obtenido · expira ${data.expires || "—"}`);
      await loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendOne = async (id) => {
    setSendingId(id);
    try {
      const { data } = await api.post(`/dgii/certification/send-case/${encodeURIComponent(id)}`);
      if (data.ok) toast.success(`${id} → TrackId ${data.track_id || "(sin id)"}`);
      else toast.error(`${id}: ${data.error}`);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingId(null); }
  };

  const sendAll = async () => {
    setSendingAll(true);
    try {
      const { data } = await api.post("/dgii/certification/send-all");
      const skipped = data.skipped_manual_upload || [];
      showSendResult(data);
      if (skipped.length > 0) {
        toast.info(
          `${skipped.length} e-CF <250K quedaron para upload MANUAL al portal: ${skipped.join(", ")}. Descarga el ZIP y súbelos en la sección "Facturas de consumo < 250Mil" DESPUÉS de que su RFCE sea aceptado.`,
          { duration: 15000 }
        );
      }
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingAll(false); }
  };

  const showSendResult = (data) => {
    if (data.stopped_reason) toast.warning(data.stopped_reason, { duration: 15000 });
    else toast.success(`${data.ok}/${data.total} casos enviados a DGII`);
  };

  const resendRfce = async () => {
    if (!window.confirm(
      "Úsalo SOLO si el portal DGII muestra \"0/4 Resúmenes de Consumo Aceptados\".\n\n" +
      "Se re-envían por API los 4 resúmenes (RFCE). Los que ya se enviaron antes se re-firman con un " +
      "código de seguridad NUEVO (DGII no acepta repetir eNCF + código).\n\n" +
      "Después descarga OTRA VEZ el \"ZIP Facturas <250K\" para subirlas al portal. ¿Continuar?"
    )) return;
    setSendingAll(true);
    try {
      const { data } = await api.post("/dgii/certification/resend-rfce");
      const bad = data.cases.filter(c => c.status !== "approved");
      if (bad.length) toast.warning(`Resúmenes aceptados: ${data.approved}/${data.cases.length}. ` + bad.map(c => `${c.encf}: ${c.last_error || c.status}`).join(" · "), { duration: 15000 });
      else toast.success(`Resúmenes aceptados: ${data.approved}/${data.cases.length}. Descarga de nuevo el "ZIP Facturas <250K" y súbelas al portal.`, { duration: 15000 });
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingAll(false); }
  };

  const restartAfterDgiiReset = async () => {
    const lastSent = cases.map(c => c.sent_at).filter(Boolean).sort().pop();
    const lastTxt = lastSent ? new Date(lastSent).toLocaleString("es-DO") : "nunca";
    if (!window.confirm(
      `Último envío de Amelia a DGII: ${lastTxt}\n\n` +
      "Úsalo SOLO si el portal DGII muestra:\n" +
      "  • un log \"Las pruebas de datos de eCF han sido reiniciadas\" con hora POSTERIOR a ese último envío, y\n" +
      "  • el contador de e-CF en 0.\n\n" +
      "Si DGII no reinició, re-enviar provoca \"[1209] secuencia ya utilizada\" y ESO sí reinicia la fase.\n\n" +
      "Se re-emitirán todos los e-CF (base → notas → resúmenes RFCE con código de seguridad nuevo).\n" +
      "Luego descarga de nuevo el \"ZIP Facturas <250K\" y súbelas al portal. ¿Continuar?"
    )) return;
    setSendingAll(true);
    try {
      const r1 = await api.post("/dgii/certification/reset-after-dgii-restart");
      toast.info(`${r1.data.reset} e-CF marcados para re-envío`);
      const { data } = await api.post("/dgii/certification/send-all");
      showSendResult(data);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingAll(false); }
  };

  const retryRejected = async () => {
    if (!window.confirm("Se re-firmará y reenviará TODOS los casos rechazados o con error.\n\n¿Continuar?")) return;
    setSendingAll(true);
    try {
      const r1 = await api.post("/dgii/certification/reset-signatures");
      toast.info(`Firmas limpiadas: ${r1.data.cleared}`);
      const { data } = await api.post("/dgii/certification/send-all");
      toast.success(`${data.ok}/${data.total} casos reenviados`);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingAll(false); }
  };

  const retryNotasWithParents = async () => {
    if (!window.confirm(
      "Esto re-emitirá las notas rechazadas Y sus comprobantes padres (NCFModificado) " +
      "en la MISMA sesión DGII, para resolver el error [614] cuando la sesión anterior expiró.\n\n" +
      "Padres aprobados se marcarán pending y se re-enviarán. ¿Continuar?"
    )) return;
    setSendingAll(true);
    try {
      const r1 = await api.post("/dgii/certification/reset-notas-parents");
      toast.info(
        `Padres reseteados: ${r1.data.cleared_parents} (${r1.data.parent_encfs.join(", ")}) · ` +
        `Notas: ${r1.data.cleared_notas}`,
        { duration: 10000 }
      );
      const { data } = await api.post("/dgii/certification/send-all");
      toast.success(`${data.ok}/${data.total} enviados — padres antes que notas en la misma sesión`);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingAll(false); }
  };

  const check = async (id) => {
    try {
      const { data } = await api.get(`/dgii/certification/case-status/${encodeURIComponent(id)}`);
      toast.success(`${id} → ${data.status} (DGII: ${data.dgii?.estado || data.dgii?.Estado || "?"})`);
      await loadCases(); loadSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = async (id) => {
    setPreviewId(id);
    setPreviewXml("…");
    try {
      const { data } = await api.get(`/dgii/certification/cases/${encodeURIComponent(id)}/xml`);
      setPreviewXml(data.xml);
    } catch (e) {
      setPreviewXml(`Error: ${formatApiError(e)}`);
    }
  };

  const clearCases = async () => {
    if (!window.confirm("¿Borrar todos los casos del Set de Pruebas?")) return;
    try {
      await api.delete("/dgii/certification/cases");
      toast.success("Casos eliminados");
      setCases([]);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadAllZip = async () => {
    try {
      toast.info("Firmando y empaquetando 29 XMLs… (puede tomar unos segundos)");
      const res = await api.get("/dgii/certification/download-all", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dgii-set-pruebas-firmado-${new Date().toISOString().slice(0,16).replace(/:/g,"")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP descargado. Incluye orden de envío oficial.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadManualUpload = async () => {
    try {
      toast.info("Firmando los 4 XMLs <250K para upload manual…");
      const res = await api.get("/dgii/certification/download-manual-upload", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturas-consumo-menor-250k-${new Date().toISOString().slice(0,16).replace(/:/g,"")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP descargado. Súbelos UNO A UNO al portal DGII en 'Facturas de consumo < 250Mil'");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openSupport = async () => {
    setSupportOpen(true);
    setSupportLoading(true);
    try {
      const { data } = await api.get("/dgii/certification/support/trace");
      setSupportData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSupportLoading(false);
    }
  };

  const downloadTraceTxt = async () => {
    try {
      toast.info("Generando trace de soporte…");
      const res = await api.post("/dgii/certification/debug/trace", null, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dgii-soporte-trace-${new Date().toISOString().slice(0,16).replace(/:/g,"")}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Trace .txt descargado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadSupportZip = async () => {
    try {
      toast.info("Empaquetando XMLs + errores DGII…");
      const res = await api.get("/dgii/certification/support/export-zip", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dgii-soporte-${new Date().toISOString().slice(0,16).replace(/:/g,"")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP de soporte descargado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyXml = async (xml, encf) => {
    if (!xml) { toast.error("Este caso aún no tiene XML firmado"); return; }
    try {
      await navigator.clipboard.writeText(xml);
      toast.success(`XML de ${encf} copiado al portapapeles`);
    } catch {
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  const downloadXml = (xml, encf) => {
    if (!xml) { toast.error("Este caso aún no tiene XML firmado"); return; }
    const blob = new Blob([xml], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${encf}.xml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Agrupaciones de contadores
  const counts = cases.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});
  const byType = cases.reduce((a, c) => {
    const k = `${c.kind}·${c.tipo_ecf}`;
    a[k] = (a[k] || 0) + 1; return a;
  }, {});

  return (
    <div className="space-y-6" data-testid="dgii-cert-tab">
      {/* Header: estado + acciones globales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Sesión DGII */}
        <div className={`p-4 rounded-md border ${session?.fresh ? "bg-success/5 border-success/30" : "bg-amber-500/5 border-amber-500/30"}`}>
          <div className="flex items-center gap-2 mb-2">
            {session?.fresh ? <CheckCircle2 className="w-4 h-4 text-success" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
            <span className="overline">Sesión DGII</span>
          </div>
          {session?.active ? (
            <>
              <div className="text-xs text-muted-foreground mb-2">
                Entorno: <b>{session.environment}</b><br/>
                Expira: {session.expires || "—"}<br/>
                Token: <span className="font-mono">{session.token_preview}</span>
              </div>
              <Button size="sm" variant="outline" onClick={authenticate} data-testid="reauth-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reautenticar
              </Button>
            </>
          ) : (
            <>
              <div className="text-xs text-muted-foreground mb-2">
                No hay sesión activa. Se iniciará sola con tu certificado al enviar o consultar; también puedes autenticar ahora.
              </div>
              <Button size="sm" onClick={authenticate} className="bg-primary" data-testid="auth-btn">
                <PlayCircle className="w-3.5 h-3.5 mr-1" /> Autenticar
              </Button>
            </>
          )}
        </div>

        {/* Set de Pruebas */}
        <div className="p-4 rounded-md border bg-card border-border md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="overline">Set de Pruebas</span>
            <span className="text-xs text-muted-foreground">
              Total: <b>{cases.length}</b> · Pendientes: <b>{counts.pending || 0}</b> ·
              Enviados: <b>{counts.sent || 0}</b> · Aprobados: <b>{counts.approved || 0}</b> ·
              Rechazados: <b>{(counts.rejected || 0) + (counts.error || 0)}</b>
            </span>
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <label className="inline-flex">
              <input type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="upload-set-input" />
              <Button
                asChild variant="outline" size="sm"
                data-testid="upload-set-btn"
              >
                <span>
                  {uploading
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Subiendo…</>
                    : <><Upload className="w-3.5 h-3.5 mr-1" /> Subir Excel DGII</>
                  }
                </span>
              </Button>
            </label>
            <Button size="sm" variant="outline" onClick={loadCases} disabled={loading} data-testid="refresh-cases-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refrescar
            </Button>
            <Button
              size="sm"
              className="bg-primary font-bold"
              onClick={sendAll}
              disabled={sendingAll || cases.length === 0}
              data-testid="send-all-btn"
            >
              {sendingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1" />}
              Enviar todos los pendientes
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadAllZip}
              disabled={cases.length === 0}
              data-testid="download-zip-btn"
              title="Firma todos y descarga ZIP (incluye README con orden de envío DGII)"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Descargar ZIP firmado
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={downloadManualUpload}
              disabled={!cases.some(c => c.status === "manual_upload")}
              data-testid="download-manual-upload-btn"
              title="Descarga sólo las 4 facturas <250K que requieren upload manual al portal DGII"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> ZIP Facturas {"<"}250K
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={refreshAllStatus}
              disabled={refreshingAll || cases.length === 0}
              data-testid="refresh-all-status-btn"
              title="Consulta a DGII el estado FINAL (Aceptado/Rechazado) de cada caso con TrackId. DGII procesa de forma asíncrona."
            >
              {refreshingAll
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Actualizar estados DGII
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={restartAfterDgiiReset}
              disabled={sendingAll || cases.length === 0}
              data-testid="dgii-restart-resend-btn"
              title="El portal DGII reinició las pruebas: re-emite todo en orden (base → notas → RFCE con código nuevo)"
            >
              {sendingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
              DGII reinició las pruebas
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={resendRfce}
              disabled={sendingAll || !cases.some(c => c.kind === "RFCE")}
              data-testid="dgii-resend-rfce-btn"
              title="El portal muestra 0/4 resúmenes aceptados: re-envía los RFCE con código de seguridad nuevo"
            >
              {sendingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Re-enviar resúmenes (RFCE)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={retryRejected}
              disabled={sendingAll || !cases.some(c => ["rejected","error"].includes(c.status))}
              data-testid="retry-rejected-btn"
              title="Re-firma y reenvía SOLO los casos rechazados o con error (usa el builder corregido)"
            >
              {sendingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Reintentar rechazados
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={retryNotasWithParents}
              disabled={sendingAll || !cases.some(c => ["rejected","error"].includes(c.status) && ["33","34"].includes(c.tipo_ecf))}
              data-testid="retry-notas-parents-btn"
              title="Para error [614]: re-emite padres (NCFModificado) + notas rechazadas en la MISMA sesión DGII"
            >
              {sendingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Reintentar notas + padres
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={openSupport}
              data-testid="open-support-trace-btn"
              title="Abre el panel de soporte DGII: estados, mensajes de rechazo y XML enviado por cada caso"
            >
              <LifeBuoy className="w-3.5 h-3.5 mr-1" /> Soporte DGII
            </Button>
            {cases.length > 0 && (
              <Button size="sm" variant="ghost" onClick={clearCases} data-testid="clear-cases-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1 text-destructive" /> Limpiar
              </Button>
            )}
          </div>

          {Object.keys(byType).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {Object.entries(byType).sort().map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[10px] font-mono">
                  {k}: {v}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabla de casos */}
      <div className="border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border bg-muted/30">
            <tr>
              <th className="text-left p-2.5">Caso</th>
              <th className="text-left p-2.5">Tipo</th>
              <th className="text-left p-2.5">Estado</th>
              <th className="text-left p-2.5">TrackId</th>
              <th className="text-left p-2.5">Error</th>
              <th className="text-right p-2.5">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <div>No hay casos cargados. Sube el .xlsx del Set de Pruebas DGII para comenzar.</div>
                </td>
              </tr>
            )}
            {cases.map((c) => (
              <tr key={c.id} className="border-b border-border/60 hover:bg-accent/30">
                <td className="p-2 font-mono text-xs">{c.encf}</td>
                <td className="p-2">
                  <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted">
                    {c.kind} {c.tipo_ecf}
                  </span>
                </td>
                <td className="p-2"><StatusBadge status={c.status} /></td>
                <td className="p-2 font-mono text-[10px] truncate max-w-[180px]" title={c.track_id || ""}>
                  {c.track_id || "—"}
                </td>
                <td className="p-2 text-[10px] text-destructive truncate max-w-[250px]" title={c.last_error || ""}>
                  {c.last_error || "—"}
                </td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openPreview(c.id)} title="Preview XML">
                    <Eye className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => sendOne(c.id)}
                    disabled={sendingId === c.id || ["manual_upload", "approved", "already_used"].includes(c.status)}
                    title={c.status === "manual_upload"
                      ? "Factura <250K: NO se envía por Recepción. Se sube manual en el portal DGII; su resumen va por RFCE"
                      : ["approved", "already_used"].includes(c.status) ? "Ya aceptado por DGII: re-enviarlo reiniciaría la fase" : "Firmar y enviar"}
                  >
                    {sendingId === c.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Send className="w-3.5 h-3.5" />
                    }
                  </Button>
                  {c.track_id && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => check(c.id)} title="Consultar estado">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview XML modal */}
      <Dialog open={!!previewId} onOpenChange={() => { setPreviewId(null); setPreviewXml(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">{previewId}</DialogTitle>
          </DialogHeader>
          <pre className="text-[11px] bg-muted/40 p-3 rounded overflow-auto max-h-[70vh] font-mono whitespace-pre-wrap break-all">
            {previewXml || "cargando…"}
          </pre>
        </DialogContent>
      </Dialog>

      {/* Soporte DGII modal */}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="max-w-6xl" data-testid="support-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LifeBuoy className="w-4 h-4" />
              Soporte DGII — estados y rechazos de comprobantes
            </DialogTitle>
          </DialogHeader>

          {supportLoading && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando reporte…
            </div>
          )}

          {!supportLoading && supportData && (
            <>
              {/* Controles globales */}
              <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3 text-xs">
                <span className="text-muted-foreground">
                  Generado: {supportData.generated_at?.slice(0, 19).replace("T", " ")} UTC
                </span>
                <span className="text-muted-foreground">·</span>
                <span>Total: <b>{supportData.total}</b></span>
                <span className="text-muted-foreground">·</span>
                {Object.entries(supportData.totals || {}).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="text-[10px] font-mono">
                    {k}: {v}
                  </Badge>
                ))}
                <div className="ml-auto flex gap-1.5">
                  <Button
                    size="sm" variant={supportFilter === "all" ? "default" : "outline"}
                    onClick={() => setSupportFilter("all")}
                    data-testid="support-filter-all"
                  >Todos</Button>
                  <Button
                    size="sm" variant={supportFilter === "rejected" ? "default" : "outline"}
                    onClick={() => setSupportFilter("rejected")}
                    data-testid="support-filter-rejected"
                  >Rechazados</Button>
                  <Button
                    size="sm" variant={supportFilter === "approved" ? "default" : "outline"}
                    onClick={() => setSupportFilter("approved")}
                    data-testid="support-filter-approved"
                  >Aprobados</Button>
                  <Button size="sm" variant="outline" onClick={downloadTraceTxt} data-testid="support-download-txt">
                    <FileSearch className="w-3.5 h-3.5 mr-1" /> Trace .txt
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadSupportZip} data-testid="support-download-zip">
                    <FileArchive className="w-3.5 h-3.5 mr-1" /> Exportar ZIP
                  </Button>
                  <Button size="sm" variant="outline" onClick={openSupport} data-testid="support-refresh">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Lista de casos */}
              <div className="overflow-auto max-h-[65vh] space-y-2 pr-1">
                {(supportData.cases || [])
                  .filter((c) => {
                    if (supportFilter === "all") return true;
                    if (supportFilter === "rejected") return ["rejected", "error"].includes(c.status);
                    if (supportFilter === "approved") return c.status === "approved";
                    return true;
                  })
                  .map((c) => {
                    const expanded = !!supportExpanded[c.id];
                    const meta = STATUS_META[c.status] || STATUS_META.pending;
                    const Icon = meta.Icon;
                    const isReject = ["rejected", "error"].includes(c.status);
                    return (
                      <div
                        key={c.id}
                        className={`border rounded-md ${isReject ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`}
                        data-testid={`support-case-${c.encf}`}
                      >
                        <div className="flex items-center gap-2 p-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${meta.color}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted">
                            {c.kind} {c.tipo_ecf}
                          </span>
                          <span className="font-mono text-xs">{c.encf}</span>
                          <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[220px]" title={c.track_id || ""}>
                            Track: {c.track_id || "—"}
                          </span>
                          <div className="ml-auto flex gap-1">
                            <Button
                              size="sm" variant="outline"
                              onClick={() => copyXml(c.signed_xml, c.encf)}
                              disabled={!c.signed_xml}
                              data-testid={`copy-xml-${c.encf}`}
                            >
                              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar XML
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => downloadXml(c.signed_xml, c.encf)}
                              disabled={!c.signed_xml}
                              data-testid={`download-xml-${c.encf}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setSupportExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}
                              data-testid={`expand-${c.encf}`}
                            >
                              {expanded ? "Ocultar" : "Detalle"}
                            </Button>
                          </div>
                        </div>

                        {c.last_error && (
                          <div className="px-3 pb-2 text-[11px] text-destructive font-medium">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            {c.last_error}
                          </div>
                        )}

                        {expanded && (
                          <div className="border-t border-border/60 p-3 space-y-2 text-[11px]">
                            {c.last_dgii_status && (
                              <div>
                                <div className="font-semibold mb-1">Respuesta DGII (cruda):</div>
                                <pre className="bg-muted/40 p-2 rounded overflow-auto max-h-[200px] font-mono whitespace-pre-wrap break-all">
                                  {JSON.stringify(c.last_dgii_status, null, 2)}
                                </pre>
                              </div>
                            )}
                            <div>
                              <div className="font-semibold mb-1">
                                XML enviado ({c.signed_xml_size || 0} bytes):
                              </div>
                              <pre className="bg-muted/40 p-2 rounded overflow-auto max-h-[320px] font-mono whitespace-pre-wrap break-all">
                                {c.signed_xml || "(aún no firmado o no enviado)"}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                {(supportData.cases || []).filter((c) => {
                  if (supportFilter === "all") return true;
                  if (supportFilter === "rejected") return ["rejected", "error"].includes(c.status);
                  if (supportFilter === "approved") return c.status === "approved";
                  return true;
                }).length === 0 && (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No hay casos que coincidan con el filtro actual.
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
